'use client';

import { trackEvent } from '@/lib/analytics';

// Firebase auth is ~140 KB. Importing it statically here dragged it into every tool
// chunk, so an anonymous visitor downloaded the whole SDK just so we could check that
// they were, in fact, anonymous. Firebase only persists a session under a
// `firebase:authUser:` key, so its absence is a reliable "no token to attach".
export async function getToolAuthToken(): Promise<string | null> {
  try {
    let signedIn = false;
    for (let i = 0; i < window.localStorage.length; i += 1) {
      const key = window.localStorage.key(i);
      if (key && key.startsWith('firebase:authUser:')) {
        signedIn = true;
        break;
      }
    }
    if (!signedIn) {
      return null;
    }

    const { firebaseAuth } = await import('@/lib/firebase-client');
    const currentUser = firebaseAuth?.currentUser ?? null;
    return currentUser ? await currentUser.getIdToken() : null;
  } catch {
    return null;
  }
}

type CreditsPayload = {
  plan?: 'guest' | 'free' | 'go';
  remaining?: number | null;
  windowLabel?: string;
};

type UsageResponse = {
  success?: boolean;
  data?: {
    credits?: CreditsPayload;
  };
  error?: unknown;
};

export async function consumeToolUsage(toolSlug: string, fallbackMessage: string) {
  const headers: HeadersInit = {
    'Content-Type': 'application/json',
  };
  const idToken = await getToolAuthToken();

  if (idToken) {
    headers.authorization = `Bearer ${idToken}`;
  }

  const usageResponse = await fetch('/api/tools/usage', {
    method: 'POST',
    headers,
    body: JSON.stringify({ toolSlug }),
  });
  const usagePayload = (await usageResponse.json().catch(() => ({}))) as UsageResponse;

  if (!usageResponse.ok || !usagePayload.success) {
    const message =
      typeof usagePayload.error === 'object' &&
      usagePayload.error &&
      'message' in usagePayload.error
        ? String((usagePayload.error as { message?: unknown }).message)
        : typeof usagePayload.error === 'string'
          ? usagePayload.error
          : fallbackMessage;
    throw new Error(message);
  }

  // Key engagement signal for a tools site: the user actually ran a tool.
  trackEvent('tool_used', { tool_slug: toolSlug });

  return usagePayload.data?.credits ?? null;
}

export function formatToolCreditSuccess(actionLabel: string, _credits?: CreditsPayload | null) {
  // Website tools are free to use, so success messages no longer surface a
  // credit balance. The credits argument is kept for call-site compatibility.
  void _credits;
  return `${actionLabel} finished.`;
}
